import * as React from "react";
import { WorkspaceTransitionOverlay, Button } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

// Stand-in for an app workspace glyph passed through the `icon` slot. The
// library never imports the app's glyphs; the consumer hands one in here.
function TeamGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="28"
      height="28"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden
    >
      <path d="M3 21V8l6-4 6 4v13" />
      <path d="M15 21V11l6 4v6" />
      <path d="M7 12h.01M7 16h.01M11 12h.01M11 16h.01" strokeLinecap="round" />
    </svg>
  );
}

// Inline SVG data URI so the avatar resolves without a network request.
const SAMPLE_AVATAR =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='96'%3E%3Crect width='96' height='96' fill='%231E8FFF'/%3E%3Ctext x='50%25' y='54%25' font-family='sans-serif' font-size='40' fill='white' text-anchor='middle'%3EM%3C/text%3E%3C/svg%3E";

export function WorkspaceTransitionOverlayDemo() {
  // One overlay instance, driven by hand so the full phase machine is visible.
  // The overlay paints fixed full-bleed (z-[9999]), so each block opens it on a
  // click and lets it auto-dismiss rather than rendering it inline.
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  // `hang` switches between a switch that resolves (1.2s) and one that never
  // does, so the safety-net fallback can be demonstrated on its own button.
  const [hang, setHang] = React.useState(false);

  const startSwitch = () => {
    setHang(false);
    setLoading(true);
    setOpen(true);
    // Simulate the async workspace switch resolving after a beat.
    window.setTimeout(() => setLoading(false), 1200);
  };

  const startHang = () => {
    setHang(true);
    setLoading(true);
    setOpen(true);
    // Never resolve: the overlay's hang fallback fires onComplete instead.
  };

  return (
    <Section
      id="workspace-transition-overlay"
      title="WorkspaceTransitionOverlay"
      description="Full-bleed blocking overlay for async transitions that must finish before the user moves on (the canonical case: switching workspaces). Runs a phase machine — opens on a spinner, morphs to a success check once loading clears, then auto-dismisses via onComplete, with a hang fallback so a stalled request never traps the user. Composes the brand Avatar tile and Spinner; sibling of LogoutLoader."
    >
      <VariantBlock
        label="Switch flow"
        caption="watch the full loading to success to auto-dismiss sequence"
      >
        <Button onClick={startSwitch}>Switch workspace</Button>
        <p className="self-center text-sm text-muted-foreground">
          {open
            ? loading
              ? "Switching…"
              : "Switched, auto-dismissing"
            : "Idle"}
        </p>
      </VariantBlock>

      <VariantBlock
        label="Hang fallback"
        caption="the switch never resolves; the safety net clears the scrim after 4s"
      >
        <Button variant="outline" onClick={startHang}>
          Simulate hang
        </Button>
      </VariantBlock>

      <VariantBlock
        label="Static snapshot"
        caption="animate={false}, avatar image wins over icon + initials"
      >
        <div className="relative h-[360px] w-full overflow-hidden rounded-lg border border-border">
          <WorkspaceTransitionOverlay
            open
            loading
            name="Maya Chen"
            type="personal"
            avatar={SAMPLE_AVATAR}
            animate={false}
            onComplete={() => {}}
            // Scope the fixed overlay to this preview box instead of the viewport.
            className="!absolute"
          />
        </div>
      </VariantBlock>

      {/* The live, interactive overlay. Rendered once at the section root so the
          two driver buttons above share it. `key` re-mounts on each new run so a
          repeat click replays cleanly from the loading phase. */}
      <WorkspaceTransitionOverlay
        key={open ? (hang ? "hang" : "switch") : "idle"}
        open={open}
        loading={loading}
        name="Acme Studio"
        type="team"
        icon={<TeamGlyph />}
        // Shorten the hang fallback so the demo shows the safety net quickly.
        timings={{ fallback: 4000 }}
        onComplete={() => setOpen(false)}
      />
    </Section>
  );
}
